import { useEffect } from 'react';
import { TreeApi } from 'react-arborist';
import { SectionsTreeNode, TREE_ROW_HEIGHT } from '@repo/atomic-components';

interface UseTreeDragHoverExpandOptions<T extends SectionsTreeNode> {
  treeApiRef: React.RefObject<TreeApi<T> | null>;
  // How long the cursor must hover over a closed row before it opens.
  hoverMs?: number;
  // Called after a section is opened (so the sticky-pin recompute can
  // pick up the change on the next frame).
  onExpanded?: () => void;
}

/**
 * Auto-expand collapsed sections when the user hovers over them for a
 * short beat mid-drag — mirrors macOS Finder's "spring-loaded folders".
 * The cursor's Y position is mapped to a visible row via the same
 * paddingTop + rowHeight arithmetic the sticky-pin hook uses. Timer
 * resets whenever the hovered row changes, and clears on dragend/drop
 * so a paused drag doesn't fire an open a second later.
 */
export const useTreeDragHoverExpand = <T extends SectionsTreeNode>({
  treeApiRef,
  hoverMs = 1000,
  onExpanded,
}: UseTreeDragHoverExpandOptions<T>): void => {
  useEffect(() => {
    let scrollEl: HTMLDivElement | null = null;
    let attachRafId = 0;
    let hoveredId: string | null = null;
    let hoverTimeoutId: number | null = null;

    const clearHoverTimer = () => {
      if (hoverTimeoutId !== null) {
        window.clearTimeout(hoverTimeoutId);
        hoverTimeoutId = null;
      }
    };

    const resetHover = () => {
      hoveredId = null;
      clearHoverTimer();
    };

    const nodeFromY = (clientY: number) => {
      const tree = treeApiRef.current;
      if (!tree || !scrollEl) return null;
      const rect = scrollEl.getBoundingClientRect();
      const offsetY = clientY - rect.top + scrollEl.scrollTop;
      const rowIndex = Math.floor(offsetY / TREE_ROW_HEIGHT);
      if (rowIndex < 0 || rowIndex >= tree.visibleNodes.length) return null;
      return tree.visibleNodes[rowIndex];
    };

    const onDragOver = (e: DragEvent) => {
      const node = nodeFromY(e.clientY);
      // Only closed internal (branch) nodes are worth expanding — leaves
      // have no children, and open sections are already showing theirs.
      if (!node || !node.isInternal || node.isOpen) {
        resetHover();
        return;
      }
      if (hoveredId === node.id) return;
      hoveredId = node.id;
      clearHoverTimer();
      hoverTimeoutId = window.setTimeout(() => {
        // Guard: node might have opened/collapsed or been removed since
        // the timer started (data refetch, keyboard nav, etc.).
        const current = treeApiRef.current?.get(node.id);
        if (current && current.isInternal && !current.isOpen) {
          current.open();
          onExpanded?.();
        }
        hoveredId = null;
      }, hoverMs);
    };

    const attach = () => {
      const el = treeApiRef.current?.listEl.current;
      if (el) {
        scrollEl = el;
        el.addEventListener('dragover', onDragOver);
        el.addEventListener('dragleave', resetHover);
        el.addEventListener('drop', resetHover);
        document.addEventListener('dragend', resetHover);
      } else {
        attachRafId = requestAnimationFrame(attach);
      }
    };
    attach();

    return () => {
      cancelAnimationFrame(attachRafId);
      clearHoverTimer();
      if (scrollEl) {
        scrollEl.removeEventListener('dragover', onDragOver);
        scrollEl.removeEventListener('dragleave', resetHover);
        scrollEl.removeEventListener('drop', resetHover);
      }
      document.removeEventListener('dragend', resetHover);
    };
  }, [treeApiRef, hoverMs, onExpanded]);
};
